/* eslint-disable no-unused-vars */
/* eslint-disable no-shadow */

export enum INTEGRATIONS_NAMES {
	GA = 'ga',
	SCRIPT = 'script',
	CUSTOM_TRACKER = 'customTracker',
	WEBHOOKS = 'webhooks',
	URL_PARAMS = 'urlParams',
	GOOGLE_PUBLISHER_TAG = 'googlePublisherTag',
}

export enum INTEGRATIONS_FIELD {
	GA_UNIVERSAL = 'gaUniversal',
	GA4 = 'ga4',
	HEAD_SCRIPT = 'headScript',
	BODY_SCRIPT = 'bodyScript',
	CUSTOM_TRACKER_URL = 'customTrackerUrl',
	URL_PARAMS_LIST = 'params',
	GOOGLE_PUBLISHER_TAG_LG = 'large',
	GOOGLE_PUBLISHER_TAG_SM = 'small',
	GOOGLE_PUBLISHER_TAG_ANDROID = 'android',
	GOOGLE_PUBLISHER_TAG_IOS = 'ios',
	GOOGLE_PUBLISHER_TAG_FIRST = 'showInitially',
	GOOGLE_PUBLISHER_TAG_BETWEEN = 'showBetweenCards',
	GOOGLE_PUBLISHER_TAG_TIMEOUT = 'timeout',
}

export enum INTEGRATIONS_PAGE_VIEW {
	DEFAULT = 'DEFAULT',
	SCRIPT = 'SCRIPT',
	GOOGLE_ANALYTICS = 'GOOGLE_ANALYTICS',
	CUSTOM_TRACKER = 'CUSTOM_TRACKER',
	WEBHOOKS = 'WEBHOOKS',
	URL_PARAMS = 'URL_PARAMS',
	GOOGLE_PUBLISHER_TAG = 'GOOGLE_PUBLISHER_TAG',
}

export enum INTEGRATIONS_SCOPE {
	STORY,
	ORGANIZATION,
}

const INTEGRATIONS_PAGE_TITLE_MAP = {
	[INTEGRATIONS_PAGE_VIEW.DEFAULT]: 'default',
	[INTEGRATIONS_PAGE_VIEW.SCRIPT]: INTEGRATIONS_NAMES.SCRIPT,
	[INTEGRATIONS_PAGE_VIEW.GOOGLE_ANALYTICS]: INTEGRATIONS_NAMES.GA,
	[INTEGRATIONS_PAGE_VIEW.CUSTOM_TRACKER]: INTEGRATIONS_NAMES.CUSTOM_TRACKER,
	[INTEGRATIONS_PAGE_VIEW.WEBHOOKS]: INTEGRATIONS_NAMES.WEBHOOKS,
	[INTEGRATIONS_PAGE_VIEW.URL_PARAMS]: INTEGRATIONS_NAMES.URL_PARAMS,
	[INTEGRATIONS_PAGE_VIEW.GOOGLE_PUBLISHER_TAG]: INTEGRATIONS_NAMES.GOOGLE_PUBLISHER_TAG,
};

export const CUSTOM_TRACKER_OPTIONS = {
	STORY_NAME: { value: '$STORY_NAME', label: 'Story name' },
	CARD_NAME: { value: '$CARD_NAME', label: 'Card name' },
	PLATFORM: { value: '$PLATFORM', label: 'Platform' },
	REFERRER: { value: '$REFERRER', label: 'Referrer' },
	RESOLUTION: { value: '$RESOLUTION', label: 'Resolution' },
} as const;

export type IntegrationListItem = {
	id: INTEGRATIONS_NAMES;
	viewId: INTEGRATIONS_PAGE_VIEW;
	icon: 'google-analytics' | 'script' | 'custom-tracker' | 'sync-progress';
	/* Path to integration field in story settings or in organization settings.
	   `version` param is required to generate path to story settings. */
	path: (version?: string) => string;
	translateKey: string;
	fields: INTEGRATIONS_FIELD[];
	/* Where integration item can be added.
	   For example, integration can be added to organization settings or to particular story. */
	scope: INTEGRATIONS_SCOPE[];
};

const INTEGRATIONS_LIST: IntegrationListItem[] = [
	{
		id: INTEGRATIONS_NAMES.GA,
		viewId: INTEGRATIONS_PAGE_VIEW.GOOGLE_ANALYTICS,
		translateKey: INTEGRATIONS_PAGE_TITLE_MAP[INTEGRATIONS_PAGE_VIEW.GOOGLE_ANALYTICS],
		icon: 'google-analytics',
		path: version =>
			version ? `storyVersions.${version}.settings.integrations.${INTEGRATIONS_NAMES.GA}` : INTEGRATIONS_NAMES.GA,
		fields: [INTEGRATIONS_FIELD.GA_UNIVERSAL, INTEGRATIONS_FIELD.GA4],
		scope: [INTEGRATIONS_SCOPE.STORY, INTEGRATIONS_SCOPE.ORGANIZATION],
	},
	{
		id: INTEGRATIONS_NAMES.SCRIPT,
		viewId: INTEGRATIONS_PAGE_VIEW.SCRIPT,
		translateKey: INTEGRATIONS_PAGE_TITLE_MAP[INTEGRATIONS_PAGE_VIEW.SCRIPT],
		icon: 'script',
		path: version =>
			version
				? `storyVersions.${version}.settings.integrations.${INTEGRATIONS_NAMES.SCRIPT}`
				: INTEGRATIONS_NAMES.SCRIPT,
		fields: [INTEGRATIONS_FIELD.HEAD_SCRIPT, INTEGRATIONS_FIELD.BODY_SCRIPT],
		scope: [INTEGRATIONS_SCOPE.STORY, INTEGRATIONS_SCOPE.ORGANIZATION],
	},
	{
		id: INTEGRATIONS_NAMES.CUSTOM_TRACKER,
		viewId: INTEGRATIONS_PAGE_VIEW.CUSTOM_TRACKER,
		translateKey: INTEGRATIONS_PAGE_TITLE_MAP[INTEGRATIONS_PAGE_VIEW.CUSTOM_TRACKER],
		icon: 'custom-tracker',
		path: version =>
			version
				? `storyVersions.${version}.settings.integrations.${INTEGRATIONS_NAMES.CUSTOM_TRACKER}`
				: INTEGRATIONS_NAMES.CUSTOM_TRACKER,
		fields: [INTEGRATIONS_FIELD.CUSTOM_TRACKER_URL],
		scope: [INTEGRATIONS_SCOPE.STORY, INTEGRATIONS_SCOPE.ORGANIZATION],
	},
	{
		id: INTEGRATIONS_NAMES.WEBHOOKS,
		viewId: INTEGRATIONS_PAGE_VIEW.WEBHOOKS,
		translateKey: INTEGRATIONS_PAGE_TITLE_MAP[INTEGRATIONS_PAGE_VIEW.WEBHOOKS],
		icon: 'sync-progress',
		path: version => '',
		fields: [],
		scope: [INTEGRATIONS_SCOPE.STORY],
	},
	{
		id: INTEGRATIONS_NAMES.URL_PARAMS,
		viewId: INTEGRATIONS_PAGE_VIEW.URL_PARAMS,
		translateKey: INTEGRATIONS_PAGE_TITLE_MAP[INTEGRATIONS_PAGE_VIEW.URL_PARAMS],
		icon: 'script',
		path: version =>
			version
				? `storyVersions.${version}.settings.integrations.${INTEGRATIONS_NAMES.URL_PARAMS}`
				: INTEGRATIONS_NAMES.URL_PARAMS,
		fields: [INTEGRATIONS_FIELD.URL_PARAMS_LIST],
		scope: [INTEGRATIONS_SCOPE.STORY],
	},
	{
		id: INTEGRATIONS_NAMES.GOOGLE_PUBLISHER_TAG,
		viewId: INTEGRATIONS_PAGE_VIEW.GOOGLE_PUBLISHER_TAG,
		translateKey: INTEGRATIONS_PAGE_TITLE_MAP[INTEGRATIONS_PAGE_VIEW.GOOGLE_PUBLISHER_TAG],
		icon: 'custom-tracker',
		path: version =>
			version
				? `storyVersions.${version}.settings.integrations.${INTEGRATIONS_NAMES.GOOGLE_PUBLISHER_TAG}`
				: INTEGRATIONS_NAMES.GOOGLE_PUBLISHER_TAG,
		fields: [
			INTEGRATIONS_FIELD.GOOGLE_PUBLISHER_TAG_LG,
			INTEGRATIONS_FIELD.GOOGLE_PUBLISHER_TAG_SM,
			INTEGRATIONS_FIELD.GOOGLE_PUBLISHER_TAG_ANDROID,
			INTEGRATIONS_FIELD.GOOGLE_PUBLISHER_TAG_IOS,
			INTEGRATIONS_FIELD.GOOGLE_PUBLISHER_TAG_FIRST,
			INTEGRATIONS_FIELD.GOOGLE_PUBLISHER_TAG_BETWEEN,
			INTEGRATIONS_FIELD.GOOGLE_PUBLISHER_TAG_TIMEOUT,
		],
		scope: [INTEGRATIONS_SCOPE.STORY, INTEGRATIONS_SCOPE.ORGANIZATION],
	},
];

class IntegrationsFacade {
	public items = INTEGRATIONS_LIST;
	public views = INTEGRATIONS_PAGE_VIEW;
	public titles = INTEGRATIONS_PAGE_TITLE_MAP;

	public getFieldById(id: INTEGRATIONS_PAGE_VIEW) {
		return this.items.find(item => item.viewId === id);
	}

	public getFields(version: string | undefined = undefined) {
		return <Record<IntegrationListItem['fields'][number], { name: string }>>this.items.reduce((acc, item) => {
			const fields = item.fields.reduce((accumulator, field: IntegrationListItem['fields'][number]) => {
				accumulator[field] = { name: `${item.path(version)}.${field}` };
				return accumulator;
			}, {});
			// eslint-disable-next-line no-param-reassign
			acc = { ...acc, ...fields };

			return acc;
		}, {});
	}
}

export default new IntegrationsFacade();
